import React, { useCallback, useMemo } from "react";

import { faCalendar } from "@fortawesome/pro-regular-svg-icons/faCalendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Radio, { RadioChangeEvent } from "antd/es/radio";
import cn from "classnames";
import { getHours, getMinutes, isSameDay, set } from "date-fns";
import { defineMessages, useIntl } from "react-intl";

import { Button, TimeDropdownPicker } from "@mapmycustomers/ui";

import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import { formatDate } from "@app/util/formatters";

import SchedulePreset from "../../enums/SchedulePreset";
import EmailSendingWindow from "../../types/EmailSendingWindow";
import createDisabledTime from "../../utils/createDisabledTime";
import { roundUpTo15Min } from "../../utils/getDefaultTodayTime";
import { PresetOption } from "../../utils/getPresetDates";

import styles from "./PresetsView.module.scss";

const messages = defineMessages({
  pickCustomDate: {
    id: "email.emailScheduleSendPopover.pickCustomDate",
    defaultMessage: "Pick a custom date",
    description: "Schedule send option to pick a custom date",
  },
  [SchedulePreset.NEXT_WEEK]: {
    id: "email.emailScheduleSendPopover.nextWeekday",
    defaultMessage: "Next {weekday}",
    description: "Schedule send preset option - next same weekday",
  },
  [SchedulePreset.TODAY]: {
    id: "email.emailScheduleSendPopover.laterToday",
    defaultMessage: "Later today",
    description: "Schedule send preset option - later today",
  },
  [SchedulePreset.TOMORROW]: {
    id: "email.emailScheduleSendPopover.tomorrow",
    defaultMessage: "Tomorrow",
    description: "Schedule send preset option - tomorrow",
  },
});

interface PresetsViewProps {
  onChange: (dateTime: Date) => void;
  onCustomDateClick: () => void;
  presets: PresetOption[];
  sendingWindow: EmailSendingWindow;
  value?: Date;
}

const getDisabledTime = (presetLabel: SchedulePreset, sendingWindow: EmailSendingWindow) =>
  createDisabledTime(
    sendingWindow,
    presetLabel === SchedulePreset.TODAY ? roundUpTo15Min(new Date()) : undefined
  );

const PresetsView = ({
  onChange,
  onCustomDateClick,
  presets,
  sendingWindow,
  value,
}: PresetsViewProps) => {
  const analytics = useExtendedAnalytics(["Schedule Send"]);
  const intl = useIntl();

  const selectedPreset = useMemo(
    () => (value ? presets.find((preset) => isSameDay(preset.dateTime, value)) : undefined),
    [presets, value]
  );

  const handleRadioChange = useCallback(
    (event: RadioChangeEvent) => {
      analytics.clicked(["Preset Date Option"]);
      const preset = presets.find(({ value }) => value === event.target.value);
      if (preset) {
        onChange(preset.dateTime);
      }
    },
    [analytics, onChange, presets]
  );

  const handleTimeChange = useCallback(
    (time: Date | null) => {
      analytics.clicked(["Preset Date Option"], { type: "Adjust Time" });

      if (time && value) {
        onChange(set(value, { hours: getHours(time), minutes: getMinutes(time) }));
      }
    },
    [analytics, onChange, value]
  );

  const weekday = useMemo(
    () =>
      formatDate(
        presets.find((preset) => preset.value === SchedulePreset.NEXT_WEEK)?.dateTime ?? new Date(),
        "EEEE"
      ),
    [presets]
  );

  const disabledTimes = useMemo(
    () =>
      new Map(
        presets.map((preset) => [preset.value, getDisabledTime(preset.value, sendingWindow)])
      ),
    [presets, sendingWindow]
  );

  const handleCustomDateClick = useCallback(() => {
    analytics.clicked(["pick custom date"]);
    onCustomDateClick();
  }, [analytics, onCustomDateClick]);

  return (
    <div className={styles.container}>
      <Radio.Group
        className={styles.presetGroup}
        onChange={handleRadioChange}
        value={selectedPreset?.value}
      >
        {presets.map((preset) => (
          <div
            className={cn(styles.presetRow, {
              [styles.presetRowSelected]: selectedPreset?.value === preset.value,
            })}
            key={preset.value}
          >
            <Radio className={styles.presetRadio} value={preset.value}>
              <div className={styles.presetLabel}>
                <span className={styles.presetTitle}>
                  {preset.value === SchedulePreset.NEXT_WEEK
                    ? intl.formatMessage(messages[SchedulePreset.NEXT_WEEK], { weekday })
                    : intl.formatMessage(messages[preset.value])}
                </span>
                <span className={styles.presetSubtitle}>{preset.subtitle}</span>
              </div>
            </Radio>
            <TimeDropdownPicker
              className={styles.presetTimePicker}
              disabled={selectedPreset?.value !== preset.value}
              disabledTime={disabledTimes.get(preset.value)}
              onChange={handleTimeChange}
              stepMinutes={15}
              timeFormat={SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm"}
              use12Hours={SHOULD_USE_12_HOUR_FORMAT}
              value={selectedPreset?.value === preset.value && value ? value : preset.dateTime}
            />
          </div>
        ))}
      </Radio.Group>
      <Button
        className={styles.customDateRow}
        leftIcon={<FontAwesomeIcon icon={faCalendar} />}
        onClick={handleCustomDateClick}
        type="text"
      >
        {intl.formatMessage(messages.pickCustomDate)}
      </Button>
    </div>
  );
};

export default PresetsView;
